import { getConversionsMetrics } from '@/views/reports/pickers/base/conversions/columns';
import { POSTBACKS_METRICS } from '@/views/reports/pickers/base/postbacks/columns';
import { ERRORS_METRICS } from '@/views/reports/pickers/base/errors/columns';
import { LIVE_VISITS_METRICS } from '@/views/reports/pickers/base/live-visits/columns';
import { LIVE_CLICKS_METRICS } from '@/views/reports/pickers/base/live-clicks/columns';
import { COMMON_METRICS } from '@/views/reports/pickers/base/columns';
import { reportTypeFromRoute } from '@/services/utils/reportType';
import { reportSearchRequestParams } from '@/store/reportColumns/report-search-params';
import _ from 'lodash';
import {
  METRICS_CUSTOM_COLUMN,
  METRICS_CUSTOM_EVENT,
  METRICS_CUSTOM_EVENT_HIDDEN_REVENUE,
  METRICS_CUSTOM_EVENT_REVENUE
} from '@/services/constants/report';


const getDefaultColumnWeight = ({ key, mainColumn }: { key: string, mainColumn?: string }) => {
  return mainColumn === key ? 300 : 100
}

const getType = (route) => {
  const { type, name } = route

  switch (name) {
    case 'app.live-clicks':
      return 'live-clicks'
    
    case 'app.live-visits':
      return 'live-visits'

    case 'app.errors':
      return 'errors'

    case 'app.conversions':
      return 'conversions'

    case 'app.postbacks':
      return 'postbacks'

    default:
      return type
  }
}

const defaultColumnsConfig = ({ allColumns, defaultSelected, defaultUnselected, order, weight }) => {
  const columns = allColumns.filter((value) => defaultSelected.includes(value) || !defaultUnselected.includes(value))

  const width = columns.reduce((acc, column) => {
    acc[column] = weight[column] || getDefaultColumnWeight({ key: column })
    return acc
  }, {})

  return {
    columns,
    order,
    width,
  }
}

const conversionsColumns = () => {
  return {
    context: 'conversions',
    ...defaultColumnsConfig(getConversionsMetrics()),
  }
}

const postbacksColumns = () => {
  return {
    context: 'postbacks',
    ...defaultColumnsConfig(POSTBACKS_METRICS),
  }
}

const errorsColumns = () => {
  return {
    context: 'errors',
    ...defaultColumnsConfig(ERRORS_METRICS),
  }
} 

const getDefaultColumnsMetric = (dimensions, metrics) => {
  const { defaultSelected, defaultUnselected } = COMMON_METRICS

  return Object.entries(dimensions).reduce((acc, [key, value]: any[]) => {
    const columns = [value.mainColumn, ...value.additionalColumns, ...metrics]
      .filter(column =>{
        if(
          column.startsWith(METRICS_CUSTOM_COLUMN) ||
          column.startsWith(METRICS_CUSTOM_EVENT) ||
          column.startsWith(METRICS_CUSTOM_EVENT_REVENUE) ||
          column.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)
        ) return false;
        return defaultSelected.includes(column) || !defaultUnselected.includes(column)
      }
    )

    acc[key] = {
      columns,
      context: key,
      width: columns.reduce((acc, column) => {
        acc[column] = getDefaultColumnWeight({ key: column, mainColumn: value.mainColumn })
        return acc
      }, {}),
      order: {
        orderBy: COMMON_METRICS.defaultSort.find(item => columns.includes(item)),
        orderDir: 'desc',
      }
    }
    return acc
  }, {})
}

export default {
  getDefaultColumns: (state) => {
    const { dimensions, metrics } = state.reportColumns

    return {
      ...getDefaultColumnsMetric(dimensions, metrics),
      conversions: conversionsColumns(),
      postbacks: postbacksColumns(),
      errors: errorsColumns(),
      'live-clicks': {
        context: 'live-clicks',
        ...defaultColumnsConfig(LIVE_CLICKS_METRICS),
      },
      'live-visits': {
        context: 'live-visits',
        ...defaultColumnsConfig(LIVE_VISITS_METRICS),
      },
    } as ColumnsSettingsContextServerFormat
  },
  getColumns: (state, getters) => {
    if (state.table.activeTab) {
      const activeTabState = state.table.activeReports[getters.activeReportIndex]?.state || {}
      return activeTabState.savedColumns || getters.savedColumnsInCurentPreset
    }

    return getters.savedColumnsInCurentPreset
  },
  groupByColumns: (state, getters) => {
    const { groupBy } = state.table.query
    const savedColumns = getters.getColumns

    const type = Array.isArray(groupBy) ? groupBy[0] : groupBy

    return savedColumns[type]?.columns || []
  },
  savedColumnsInCurentPreset: (state) => {
    return state.reportColumns.savedColumns[state.report.activePreset]
  },
  reportParams: (state, getters) => {
    const { dimensions } = state.reportColumns
    const savedColumns = getters.getColumns
    const { route } = state.report

    const type = getType(route)
    const searchParams = reportSearchRequestParams(getters.tableQuerySearch)

    const {filterHaving, filterHavingOptions, filterHavingValues}
      =  getters.tableColumnsFiltersForCurrentTabInQueryFormat;
    const groupBy = reportTypeFromRoute();
    const {
      compareEnabled,
      compareDate,
      from,
      to,
      timezone,
      compareFrom,
      compareTo,
      date,
      treeLevels,
      tree,
      view,
      currency,
      formatResponse = 1,
      limit,
      offset = {},
    } = getters.tableQueryForCurrentTab

    const {
      status,
      postbackStatus
    } = state.table.query;

    if(!savedColumns){
      return;
    }
    const {
      columns: curColumns = [],
      order = {},
    } = savedColumns[type] || {}
    const { mainColumn } = dimensions[route.type] || {}

    let columns = _.uniq([...(mainColumn ? [mainColumn] : []), ...curColumns])

    const commonValue = {
      currency,
      date,
      from,
      compareFrom,
      compareTo,
      compareDate,
      compareEnabled,
      limit,
      offset:offset[groupBy],
      timezone,
      to,
      formatResponse,
      filterHaving,
      filterHavingOptions,
      filterHavingValues,
    }

    switch (type) {
      case 'conversions':
        return {
          ...commonValue,
          columns,
          groupBy: 'conversions',
          orderBy: order.orderBy || 'conversions-postbacktimestamp',
          orderDir: order.orderDir || 'desc',
          ...(searchParams.searchBy === 'text' ? searchParams : {}),
        }
      case 'postbacks':
        return {
          ...commonValue,
          columns: columns,
          groupBy: 'postbacks',
          orderBy: order.orderBy || 'postbacks-timestamp',
          orderDir: order.orderDir || 'desc',
          ...(postbackStatus === 'all' ? {} : {postbackStatus:postbackStatus}),
          ...(searchParams.searchBy === 'text' ? searchParams : {}),
        }
      case 'errors':
        return {
          ...commonValue,
          columns: columns,
          groupBy: 'errors',
          orderBy: order.orderBy || 'errors-timestamp',
          orderDir: order.orderDir || 'desc',
          ...(searchParams.searchBy === 'text' ? searchParams : {}),
        }
      case 'live-visits':
        return {
          ...commonValue,
          columns: columns,
          groupBy: 'live-visits',
          orderBy: order.orderBy || 'lives-timestamp',
          orderDir: order.orderDir || 'desc',
        }
      case 'live-clicks':
        return {
          ...commonValue,
          columns: columns,
          groupBy: 'live-clicks',
          orderBy: order.orderBy || 'lives-timestamp',
          orderDir: order.orderDir || 'desc'
        }
      default:
        return {
          ...order,
          ...commonValue,
          columns,
          from,
          groupBy,
          status,
          ...searchParams,
        }
    }
  },
  reportParamsByTabIdAndGroupBy: (state, getters) => {
    return ({ groupBy, tabId = state.table.activeTab})=>{
      let tableQueryPath = state.table.query;
      if(tabId > 0){
        tableQueryPath = state.table.activeReports?.find((item)=>item.tabId === tabId)?.state?.tableQuery
      }
      if(!tableQueryPath){
        return;
      }
      const tableQuerySearch = ()=>{
        if(tabId !==0){
          return state.table.activeReports[tabId]?.state?.tableQuery?.search || {}
        }
        switch(state.user.reportSearchSetting) {
          case 'perTab':
            return state.table.query?.search?.[groupBy]  || {};
          case 'allTabs':
          default:
            return state.table.query.search || {}
        }
      }

      const searchState = tableQuerySearch() || {};
      const {
        currency,
        date,
        from,
        compareFrom,
        compareTo,
        compareDate,
        compareEnabled,
        limit,
        offset = {},
        status,
        timezone,
        to,
        view,
        formatResponse,
        filterHaving,
        filterHavingOptions,
        filterHavingValues
      } = tableQueryPath

      const tableQueryData = {
        currency,
        date,
        from,
        compareFrom,
        compareTo,
        compareDate,
        compareEnabled,
        limit,
        offset: offset[groupBy],
        status,
        timezone,
        to,
        view,
        formatResponse,
        filterHaving,
        filterHavingOptions,
        filterHavingValues
      }

      switch (groupBy) {
        case 'conversions':
        case 'postbacks':
        case 'errors':
        case 'live-visits':
        case 'live-clicks':
          return {
            ...tableQueryData,
            ...reportSearchRequestParams(searchState, { textExact: true }),
          }
        default:
          return {
            ...tableQueryData,
            ...reportSearchRequestParams(searchState),
          }
      }
    }
  },
  supportedMetricsAsObject: (state, getters) => {
    const res = {}
    state.reportColumns.metrics.forEach((column) => res[column] = true)
    return res
  }
};
